import { cookieExists, deleteCookie, getCookie, removeCookie, setCookie } from "@ciaopeople/cp-shatsu/Cookie";
import { SimpleEventHandler } from "@ciaopeople/cp-shatsu/EventHandler";
import { Utils } from "@ciaopeople/cp-shatsu/HTTP";
import { Logger } from "@ciaopeople/cp-shatsu/Logging";
import { isDefined, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { hasOwnProperty } from '@ciaopeople/cp-shatsu/Utils';
import { CookieConfig, CookieNamesConfig } from "../../config/PropertyConfig.js";
import { UserRole } from "../../login/UserRole.js";
import { CookieChangeEventData, COOKIE_CHG_EVENT, isCookieChangeEventData, SessionHelperOptions, SessionStorageInterface } from "../SessionStorageInterface.js";
import { StorableSession } from "../StorableSession.js";

const VERSION_COOKIE_NAME = "apicookup";
const DEFAULT_COOKIE_PATH = "/";


/**
 *
 * Questa classe viene utilizzata per racchiudere tutte le operazioni
 * necessarie alla gestione della sessione dell'utente sotto la API SDK
 *
 * @param cookies contiene le informazioni relative ai nomi dei cookie da utilizzare
 * @param cookieDomain dominio dei cookie che verranno scritti dal gestore
 * @constructor
 * @example
 *
 *      var sessfun = new SessionFunctions({
 *          session: "fp_ut",
 *          logout: "fp_lo"
 *      },".fanpage.it");
 *
 */
export class SessionStorageCookieImpl extends SimpleEventHandler implements SessionStorageInterface {
    
    /**
     * Hashmap dei job di monitoraggio
     */
    private monitors: { [k:string]: number } = {};
    /**
     * Hashmap degli eventi da interrompere
     */
    private monitorstop: { [k:string]:boolean } = {};
    /**
     * Hashmap per tracciare gli ultimi valori triggerati dai cookies
     */
    private lastvalues: { [k:string]:string } = {};

    constructor(
        private cookies: CookieConfig,
        private options: SessionHelperOptions,
        private logger: Logger
    ) {
        super();
        if (!hasOwnProperty(this.options, "monitorFrequency")) {
            this.options.monitorFrequency = 500;
        }
        // this.lastvalues = {};
        // this.versionCookieName = "apicookup";
        // this.currentCookieVersion = !isNaN(parseInt(cookiesVersion))?cookiesVersion:null;
    }
    getFacebookLoginToken(): Promise<string> {
        const exists = cookieExists(this.cookies.names.fbToken);
        const kget = getCookie(
            this.cookies.names.fbToken
        );
        return Promise.resolve(exists && kget && kget != "null"?kget:null);
    }
    setFacebookLoginToken(tk: string): Promise<void> {
        setCookie(
            this.cookies.names.fbToken,
            1,
            tk,
            this.cookies.cookieDomain,
            DEFAULT_COOKIE_PATH,
            true,
            "None"
        );
        return Promise.resolve();
    }
   

    private isMonitored(cookieName: keyof CookieNamesConfig) {
        return (cookieName in this.monitors);
    }

    private isManaged(cookieName: keyof CookieNamesConfig) {
        return (cookieName in this.cookies.names);
    }

    public onCookieChange(f: (cookieChangeEvent: CookieChangeEventData) => void): void {
        this.on(COOKIE_CHG_EVENT, (evt, data) => {
            this.logger.debug("Cookie change event detected (SessionStorage)", data);
            if(isCookieChangeEventData(data)) f(data);
        });
    }
    
    private monitorCookie(cookieName: keyof CookieNamesConfig): number {
        return window.setTimeout(
            () => {
                const cookieVal = getCookie( this.cookies.names[cookieName]);
                // this.logger.debug("Cookievalue was ", this.lastvalues[cookieName], " now is ", cookieVal);

                if (isString(cookieVal)) {
                    if (cookieVal != this.lastvalues[cookieName]) {
                        this.logger.log("Cookie " + cookieName + " changed from ", this.lastvalues[cookieName], "to", cookieVal);
                        this.trigger(
                            COOKIE_CHG_EVENT,
                            {
                                name: cookieName,
                                val: cookieVal,
                                oldval: this.lastvalues[cookieName]
                            }
                        );
                        this.lastvalues[cookieName] = cookieVal;
                    }
                }
                if (this.monitorstop[cookieName] !== true ) {
                    this.monitors[cookieName] = this.monitorCookie(cookieName);
                }
                else {
                    this.logger.debug("stopping monitoring for ", cookieName);
                }
            },
            this.options.monitorFrequency
        );
    }

    /**
     * Inizializza il monitoring di un cookie.
     * Quando il valore di questo cookie cambia viene lanciato un evento
     * "cookiechange" del tipo [[CookieChangeEventData]]
     *
     * La frequenza di monitoraggio è data da *monitorFrequency* (default 500ms).
     * 
     * @param cookieName nome del cookie da monitorare
     */
    public startCookieMonitoring(cookieName: keyof CookieNamesConfig): Promise<void> {
        if( !this.isMonitored(cookieName) && this.isManaged(cookieName) ) {
            this.logger.debug("Started Montioring of cookie " + cookieName);


            this.monitorstop[cookieName] = false;
            this.lastvalues[cookieName] = getCookie( this.cookies.names[cookieName]);


            this.monitors[cookieName] = this.monitorCookie(cookieName);
        }
        return Promise.resolve();
    }


    /**
     * Complementare di [[startCookieMonitoring]],
     * interrompere il ciclo di monitoraggio di un determinato cookie.
     * @param cookieName nome del cookie su cui interrompere il monitoraggio
     */
    public stopCookieMonitoring(cookieName: keyof CookieNamesConfig): Promise<void> {
        if (this.isManaged(cookieName)) {
            try {
                this.monitorstop[cookieName] = true;
                this.logger.debug("clear monitorstop", this.monitorstop);
                delete this.monitors[cookieName];
                this.logger.debug("monitors is", this.monitors, "to stop", cookieName, this.monitors[cookieName]);
            }
            catch (e) {
                this.logger.error("timeout stop error", e);
            }
        }
        return Promise.resolve();
    }


    /**
     * indentifica se i cookie sono stati mai settati per la sessione
     * @throws {Error} se la classe non gestisce il cookie 'session'
     */
    public thereWasASession(): Promise<boolean> {
        const hadSessionCookie = cookieExists(this.cookies.names.session) && getCookie(this.cookies.names.session) !== "";
        this.logger.debug("There was session check:", hadSessionCookie, this.isAutologinLocked());
        return Promise.resolve( hadSessionCookie || this.isAutologinLocked() );
    }


    /**
     * Verifica se il blocco dell'autologin è impostato.
     * 
     * @return true se l'autologin è bloccato
     */
    public isAutologinLocked(): Promise<boolean> {
        return Promise.resolve(cookieExists(this.cookies.names.logout) && getCookie(this.cookies.names.logout) !== "null");
    }

    /**
     * Imposta il blocco dell'autologin impostando il cookie di logout.
     * Il cookie viene impostato con una scadenza di 3 giorni
     */
    public blockAutologin(): Promise<void> {
        return this.internalAutologinBlock(3);
    }

    public blockAutologinLonger(): Promise<void> {
        return this.internalAutologinBlock(365);
    }

    private internalAutologinBlock(timeInDays: number): Promise<void> {
        setCookie(
            this.cookies.names.logout,
            timeInDays,
            "ufrada",
            this.cookies.cookieDomain,
            DEFAULT_COOKIE_PATH,
            true,
            "None"
        );
        return Promise.resolve();
    }

    /**
     * Rimuovoe il blocco di autologin, eliminando il cookie di logout
     */
    public unlockAutologin(): Promise<void> {
        deleteCookie(this.cookies.names.logout, this.cookies.cookieDomain, DEFAULT_COOKIE_PATH);
        return Promise.resolve();
    }


    public getLoginPreviousState(): Promise<string> {
        return Promise.resolve(
            cookieExists(this.cookies.names.loginPreviousState) ? (
                getCookie(this.cookies.names.loginPreviousState) == "1" ? "1": "0"
            ) : "-"
        );
    }
    


    public setLoginPreviousState(loggedIn: boolean): Promise<void> {
        setCookie(
            this.cookies.names.loginPreviousState,
            1000,
            loggedIn?"1":"0",
            this.cookies.cookieDomain,
            DEFAULT_COOKIE_PATH,
            true,
            "None"
        );
        return Promise.resolve();
    }


    public setLastLoginProvider(provider: string): Promise<void> {
        this.logger.debug("SetLoginProvider() - retrieved value:", provider);
        setCookie(
            this.cookies.names.lastLoginProvider,
            1000,
            provider,
            this.cookies.cookieDomain,
            DEFAULT_COOKIE_PATH,
            true,
            "None"
        );
        return Promise.resolve();
    }

    public getLastLoginProvider(): Promise<string> {
        const ck = getCookie(this.cookies.names.lastLoginProvider);
        this.logger.debug("GetLoginProvider() - retrieved value:", ck, "cookie exists?", cookieExists(this.cookies.names.lastLoginProvider));
        return Promise.resolve(
            cookieExists(this.cookies.names.lastLoginProvider) && ck !== "" ? ck : "UNK"
        );
    }







    /**
     * Annulla il login facebook in corso
     */
    public cancelFacebookLogin(): Promise<void> {
        return this.setFacebookLoginToken("cancel");
    }



    /**
     * Annulla il login facebook in corso
     */
    public clearFacebookLoginToken(): Promise<void> {
        deleteCookie(this.cookies.names.fbToken, this.cookies.cookieDomain, DEFAULT_COOKIE_PATH);
        return Promise.resolve();
    }

    /**
     * Verifica se il cookie del facebook token è ancora vuoto o inesistente
     * @return vero se vuoto o inesistente
     */
    public isFacebookTokenClean(): Promise<boolean> {
        return Promise.resolve(
            !cookieExists(this.cookies.names.fbToken) || getCookie(this.cookies.names.fbToken) == ""
        );
    }

    /**
     * imposta il cookie di versione
     */
    public cookieVersionStore(): Promise<void> {
        setCookie(
            VERSION_COOKIE_NAME,
            365,
            ""+this.cookies.cookieversion,
            this.cookies.cookieDomain,
            DEFAULT_COOKIE_PATH,
            true,
            "None"
        );
        return Promise.resolve();
    }

    /**
     * verifica se il cookie di sessione esiste e deve essere aggiornato
     */
    public shouldUpdateCookies(): Promise<boolean> {
        if (this.cookies.cookieversion) {
            return Promise.resolve(
                !cookieExists(VERSION_COOKIE_NAME) || isNaN( parseInt(getCookie(VERSION_COOKIE_NAME)) )
            );
        }
        else {
            return Promise.resolve(false);
        }
    }






    /**
     * Ritorna la sessione utente (se esistente).
     * L'oggetto ritornato è di questo tipo:
     *      {
     *             token: "tokenDiAutenticazione",
     *             role: 2
     *      }
     * se la sessione non esiste torna null
     * 
     * @return oggetto che rappresenta la sessione
     */
    public getSession(): Promise<StorableSession|null> {
        try {
            const parsedCookieValue = Utils.unparam(getCookie(this.cookies.names.session));
            if (isObject(parsedCookieValue)) {
                if (hasOwnProperty(parsedCookieValue, "T") && 
                        isString(parsedCookieValue['T']) && 
                        hasOwnProperty(parsedCookieValue, "R") &&
                        isDefined(parsedCookieValue['R'])
                ) {
                    return this.isAutologinLocked().then(
                        v => {
                            let res = null;
                            if (!v) {
                                res = {
                                    token: parsedCookieValue['T'],
                                    role: UserRole.AUTHENTICATED_USER
                                };
                            }
                            return Promise.resolve(res);
                        }
                    );
                }
            }
        }
        catch(e) {
            this.logger.warn(e);
        }
        return Promise.resolve(null);
    }




    /**
     * Memorizza l'oggetto che rappresenta la connessione in un cookie.
     * il cookie che viene settato ha una scadenza di 1000 giorni
     * 
     * @param session Oggetto che rappresenta la sessione
     * @param {Int} session.role ruolo dell'utente che ha accesso alla sessione
     * @return {Object} oggetto che rappresenta la sessione
     */
    public storeSession(session: StorableSession): Promise<void> {
        if (isObject(session) && isString(session.token)) {
            setCookie(
                this.cookies.names.session,
                1000,
                Utils.param({
                    T: session.token,
                    R: session.role
                }),
                this.cookies.cookieDomain,
                DEFAULT_COOKIE_PATH,
                true,
                "None"
            );
        }
        else {
            this.logger.info("nothing to store");
        }
        return Promise.resolve();
    }

    /**
     * Cancella il cookie in cui è memorizzata la sessione
     */
    public clearSession(): Promise<void> {
        removeCookie(this.cookies.names.session, this.cookies.cookieDomain, DEFAULT_COOKIE_PATH);
        return Promise.resolve();
    }


    public storeSessionAndProvider(session: StorableSession, provider: string): Promise<void> {
        return Promise.all([
            this.storeSession(session),
            this.setLastLoginProvider(provider)
        ]).then();
    }

}

